import "./style.scss";

import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import { Grid } from "@mui/material";
import InsertPhoto from "assets/img/svg/insertPhoto";
import classNames from "classnames";
import { getValueOrDraft } from "components/Beneficiar/utils";
import { labelFor } from "components/BeneficiaryFormContainer/PersonalInfoStep";
import { displayMobileNavBtn } from "components/shared/BPMobileNavBtn";
import type { TMissingInfos } from "components/shared/BPNavigationBarSettings";
import LabelComponent from "components/shared/LabelComponent";
import LocaleAutocomplete from "components/shared/NameTranslationWrapper/LocaleAutocomplete";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { placeTextAreaAtEnd } from "containers/CustomFieldsContainer/helpers";
import RenderIf from "helpers/RenderIf";
import { getSocialSecurityPlaceholder } from "helpers/utils";
import { useIsMobile } from "hooks/useIsMobile";
import type { ChangeEvent, SetStateAction } from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TEntity } from "store/beneficiaryStore";
import type { ICustomField, StoreCustomField } from "store/customFieldsStore";
import { useAppSelector } from "store/store";

import type { CountryFeatures } from "types/countryFeatures";
import type { TEditingBeneficiarProfile } from "../../../BPSettings/types";
import BeneficiaryCustomFields from "../BeneficiaryCustomFields";
import MissingInfosWarning from "../MissingInfosWarning";
import { getMissingFieldsNames } from "../utils";

type TProps = {
    /**
     * Data of the specific page.
     */
    data: TEditingBeneficiarProfile["account"] | undefined;
    /**
     * Know if changes are allowed
     */
    allowChanges: boolean;
    /**
     * Modifies editingProfile whenever there is a change that is made to reflect it visually and value is ready in case changes are saved.
     * @param ev Data of event triggered.
     * @param data Data of the specific page (so editingProfile.account for instance).
     * @param customFieldsInfo Data needed to update + format CF: data = fullCustomFieldsData, field = field saved in Benef info, fullFieldData = data of CF as saved in store.
     */
    handleChange: (
        ev: any,
        data: any,
        customFieldsInfo?: { data: any; field: any; fullFieldData?: ICustomField },
    ) => void;
    handleUpload: (ev: ChangeEvent<HTMLInputElement>) => void;
    /**
     * Custom fields existing on this company:
     */
    companyBenefCustomFields: ICustomField[] | undefined;
    /**
     * Handles saving fileData into editingProfile value:
     */
    handleFileSaving: (data: any, fileData: string, field: any, fullFieldData: ICustomField) => void;
    /**
     * Custom fields data that exist on company with the benef info:
     */
    customFieldsData: StoreCustomField[];
    /**
     * Verify if the author got invited to complete their profile
     */
    isInvited: boolean;
    /**
     * If has fields with errors
     */
    setHasFieldErrors: React.Dispatch<SetStateAction<boolean>>;
    /**
     * On sending form, if errors, should recalulate errors to highlight fields in red
     */
    recalculateErrors: boolean;
    /**
     * Change state to recalculate fields errors
     */
    setRecalculateErrors: React.Dispatch<SetStateAction<boolean>>;
    /**
     * Beneficiary's entity type to properly display labels and fields;
     */
    entityType: TEntity;
    /**
     * Set fields errors
     */
    setFieldErrors: React.Dispatch<SetStateAction<string[]>>;
    /**
     * Fields errors
     */
    fieldErrors: string[];
    countryFeatures: CountryFeatures;
};

const BPAccount = ({
    data,
    allowChanges,
    handleChange,
    handleUpload,
    companyBenefCustomFields,
    handleFileSaving,
    customFieldsData,
    isInvited,
    setHasFieldErrors,
    recalculateErrors,
    setRecalculateErrors,
    entityType,
    setFieldErrors,
    fieldErrors,
    countryFeatures,
}: TProps) => {
    const [t] = useTranslation();
    const { isMobile } = useIsMobile();
    const { missingData, beneficiarPersonalInfo } = useAppSelector((state) => state.beneficiary);

    /**
     * Custom fields specific to Account page.
     */
    const accountCustomFields = useMemo(() => {
        const neededFields = companyBenefCustomFields?.filter((field) => field.formLocation !== "accounting");
        return placeTextAreaAtEnd(neededFields);
    }, [companyBenefCustomFields]);

    const emptyMandatoryCustomFields =
        accountCustomFields?.filter(
            (field) =>
                !!field.isRequired &&
                !customFieldsData?.find((fieldData) => fieldData.customFieldTypeId === field.id)?.value,
        ) ?? [];

    const emptyMandatoryFields = ["name", "surname", ...missingData].filter((field) => !getValueOrDraft(data?.[field]));

    const missingCustomFieldsData = emptyMandatoryCustomFields?.map((customField) => String(customField.id)) ?? [];
    const defaultRequiredFields = ["name", "surname", ...missingData, ...missingCustomFieldsData];

    const emptyFieldsErrors = [...emptyMandatoryCustomFields, ...emptyMandatoryFields];

    useEffect(() => {
        setHasFieldErrors(!!emptyFieldsErrors.length);
    }, [emptyMandatoryCustomFields, emptyMandatoryFields]);

    useEffect(() => {
        if (!recalculateErrors) return;
        defaultRequiredFields.forEach((fieldName) => {
            const isCustomField = !!+fieldName;
            if (isCustomField) {
                const hasFieldError = emptyMandatoryCustomFields?.some((field) => String(field.id) === fieldName);
                if (hasFieldError) {
                    setFieldErrors((prev) => [...prev.filter((value) => value !== fieldName), fieldName]);
                }
            } else {
                if (!getValueOrDraft(data?.[fieldName])) {
                    setFieldErrors((prev) => [...prev.filter((value) => value !== fieldName), fieldName]);
                }
            }
        });
        setRecalculateErrors(false);
    }, [recalculateErrors]);

    const hasEmptyMandatoryInfo = isInvited && !!emptyFieldsErrors.length;
    const missingFieldsNames: string[] = getMissingFieldsNames(emptyMandatoryCustomFields, emptyMandatoryFields);

    const missingInfos: TMissingInfos = {
        account: missingFieldsNames,
    };

    const isRequired = (fieldName: string) => {
        return defaultRequiredFields.includes(fieldName);
    };

    /** Remove fields error, if mandatory fields have data */
    useEffect(() => {
        defaultRequiredFields.forEach((fieldName) => {
            if (data?.[fieldName]) {
                setFieldErrors((prev) => prev.filter((field) => fieldName !== field));
            }
        });
    }, [data]);

    /** Remove fields error, if mandatory custom fields have data */
    useEffect(() => {
        fieldErrors.forEach((fieldName) => {
            const isCustomField = !!+fieldName;
            if (!isCustomField) return;
            const hasFieldError = emptyMandatoryCustomFields.some((field) => String(field.id) === fieldName);
            if (!hasFieldError) {
                setFieldErrors((prev) => prev.filter((field) => field !== fieldName));
            }
        });
    }, [emptyMandatoryCustomFields]);

    /**
     * Render required props fields
     * @param fieldName name of field
     */
    const requiredFieldProps = (fieldName: string) => {
        const showError = fieldErrors.includes(fieldName);
        if (showError) {
            return {
                error: true,
                helperText: t("Beneficiars.ThisFieldIsRequired"),
            };
        }
    };

    /**
     * On blur field, set error if the field is empty
     * @param e focus event
     * @param fieldName field name
     */
    const handleFieldError = (e: any, fieldName: string) => {
        if (!e.target.value && isRequired(fieldName)) {
            setFieldErrors((prev) => [...prev.filter((value) => value !== fieldName), fieldName]);
        }
    };

    const renderInputDatePicker = (params: any) => {
        return (
            <StyledTextField
                placeholder="yyyy"
                fullWidth
                {...params}
                InputProps={{
                    endAdornment: (
                        <>
                            {isMobile ? (
                                <InsertInvitationOutlinedIcon
                                    style={{ color: "#A8A8A8", height: "20px", width: "20px" }}
                                />
                            ) : (
                                params.InputProps?.endAdornment
                            )}
                        </>
                    ),
                }}
            />
        );
    };

    return (
        <div className="bp-settings-info">
            {hasEmptyMandatoryInfo && (
                <div className="bp-settings-info-bloc" style={{ marginBottom: "0" }}>
                    <MissingInfosWarning
                        tooltipTitle={missingFieldsNames.join(", ")}
                        from="account"
                        missingInfos={missingInfos}
                    />
                </div>
            )}
            <div className="bp-settings-info-bloc">
                <h3 style={{ marginTop: "2.75rem" }}>{t("Settings.Identity")}</h3>
                <div className="bp-settings-account-avatar-wrapper">
                    <LabelComponent htmlFor="">{labelFor()[entityType]?.avatarUrl}</LabelComponent>
                    <div className="bp-settings-account-avatar-field">
                        <div className="bp-settings-account-avatar">
                            {data?.avatarUrl ? <img src={data.avatarUrl} alt="avatar" /> : <InsertPhoto />}
                            <input
                                type="file"
                                id="profilePicture"
                                name="avatarUrl"
                                style={{ display: "none" }}
                                onChange={(ev) => handleUpload(ev)}
                                accept="image/jpeg, image/jpg, image/png, image/gif"
                            />
                        </div>
                        <div>
                            <label className="bp-settings-account-avatar-label" htmlFor="profilePicture">
                                {t("Settings.UploadPicture")}
                            </label>
                            <p>{t("Settings.FormatPicture")}</p>
                        </div>
                    </div>
                </div>
                <Grid container rowSpacing={4} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        <LabelComponent htmlFor="name" className={classNames({ "field-required": isRequired("name") })}>
                            {labelFor()[entityType]?.name}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("name")}
                            placeholder={t("FormPlaceholders.FirstName")}
                            value={data ? data.name : ""}
                            name="name"
                            id="name"
                            fullWidth
                            onBlur={(e) => handleFieldError(e, "name")}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelComponent
                            htmlFor="surname"
                            className={classNames({ "field-required": isRequired("surname") })}
                        >
                            {labelFor()[entityType]?.surname}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("surname")}
                            placeholder={t("FormPlaceholders.LastName")}
                            value={data ? data.surname : null}
                            name="surname"
                            id="surname"
                            fullWidth
                            onBlur={(e) => handleFieldError(e, "surname")}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    {entityType === "physical" && (
                        <>
                            <Grid item xs={12} md={6}>
                                <LabelComponent htmlFor="birthDate">{t("NewBeneficiary.BirthDate")}</LabelComponent>
                                <StyledDatePicker
                                    value={data ? data.birthDate : ""}
                                    inputFormat="dd/MM/yyyy"
                                    renderInput={renderInputDatePicker}
                                    onOpen={() => isMobile && displayMobileNavBtn(false)}
                                    onClose={() => isMobile && displayMobileNavBtn(true)}
                                    onChange={(date) => {
                                        handleChange({ target: { name: "birthDate", value: date } }, { ...data });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <LabelComponent
                                    htmlFor="socialSecurityNumber"
                                    className={classNames({ "field-required": isRequired("socialSecurityNumber") })}
                                >
                                    {t("Beneficiars.SecurityNumber")}
                                </LabelComponent>
                                <StyledTextField
                                    {...requiredFieldProps("socialSecurityNumber")}
                                    placeholder={getSocialSecurityPlaceholder(
                                        countryFeatures.validation.socialSecurityFormat,
                                    )}
                                    value={data ? getValueOrDraft(data.socialSecurityNumber) : ""}
                                    name="socialSecurityNumber"
                                    id="socialSecurityNumber"
                                    fullWidth
                                    onBlur={(e) => handleFieldError(e, "socialSecurityNumber")}
                                    onChange={(ev) => handleChange(ev, { ...data })}
                                ></StyledTextField>
                            </Grid>
                        </>
                    )}
                </Grid>
            </div>
            <div className="bp-settings-info-bloc">
                <h3>{t("Settings.Contact")}</h3>
                <Grid container rowSpacing={4} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        <LabelComponent htmlFor="">{t("Beneficiars.Email")}</LabelComponent>
                        <StyledTextField
                            placeholder="example@crealo.app"
                            value={data ? data.email : ""}
                            name="email"
                            fullWidth
                            disabled={true}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelComponent
                            htmlFor="phoneNumber"
                            className={classNames({ "field-required": isRequired("phoneNumber") })}
                        >
                            {t("Beneficiars.PhoneNumber")}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("phoneNumber")}
                            placeholder={t("FormPlaceholders.Phone")}
                            value={data ? getValueOrDraft(data.phoneNumber) : ""}
                            name="phoneNumber"
                            id="phoneNumber"
                            fullWidth
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LocaleAutocomplete
                            {...requiredFieldProps("lang")}
                            value={data ? getValueOrDraft(data.lang) : ""}
                            onChange={(ev, value) => handleChange({ target: { name: "lang", value } }, { ...data })}
                        />
                    </Grid>
                </Grid>
            </div>
            <div className="bp-settings-info-bloc">
                <h3>{t("Settings.Address")}</h3>
                <Grid container rowSpacing={4} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        <LabelComponent
                            htmlFor="street"
                            className={classNames({ "field-required": isRequired("street") })}
                        >
                            {t("NewBeneficiary.StreetNumber")}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("street")}
                            placeholder={t("AddressPlaceholders.Street")}
                            value={data ? getValueOrDraft(data.street) : ""}
                            name="street"
                            id="street"
                            fullWidth
                            onBlur={(e) => handleFieldError(e, "street")}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelComponent
                            htmlFor="zipCode"
                            className={classNames({ "field-required": isRequired("zipCode") })}
                        >
                            {t("NewBeneficiary.ZIPCode")}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("zipCode")}
                            placeholder={t("AddressPlaceholders.ZipCode")}
                            value={data ? getValueOrDraft(data.zipCode) : ""}
                            name="zipCode"
                            id="zipCode"
                            fullWidth
                            onBlur={(e) => handleFieldError(e, "zipCode")}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelComponent htmlFor="city" className={classNames({ "field-required": isRequired("city") })}>
                            {t("NewBeneficiary.City")}
                        </LabelComponent>
                        <StyledTextField
                            {...requiredFieldProps("city")}
                            placeholder={t("AddressPlaceholders.City")}
                            value={data ? getValueOrDraft(data.city) : ""}
                            name="city"
                            id="city"
                            fullWidth
                            onBlur={(e) => handleFieldError(e, "city")}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelComponent htmlFor="country">{t("NewBeneficiary.Country")}</LabelComponent>
                        <StyledTextField
                            placeholder={t("AddressPlaceholders.Country")}
                            value={data ? data.country : ""}
                            name="country"
                            id="country"
                            fullWidth
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Grid>
                </Grid>
            </div>
            <RenderIf show={beneficiarPersonalInfo.entity === "legal"}>
                <div className="bp-settings-info-bloc">
                    <h3>{t("Titles.Informations")}</h3>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <LabelComponent htmlFor="siretNumber">{t("Beneficiars.Siret")}</LabelComponent>
                            <StyledTextField
                                placeholder={t("NewBeneficiary.CompanyCodeHere")}
                                disabled={!allowChanges}
                                value={data ? getValueOrDraft(data.siretNumber) : ""}
                                name="siretNumber"
                                id="siretNumber"
                                onChange={(ev) => handleChange(ev, { ...data })}
                            ></StyledTextField>
                        </Grid>
                    </Grid>
                </div>
            </RenderIf>
            {accountCustomFields && accountCustomFields.length > 0 && (
                <div className="bp-settings-info-bloc custom-fields">
                    <h3>{t("BeneficiaryPortal.AccountPage.OtherInfo")}</h3>
                    <Grid container rowSpacing={4} columnSpacing={2}>
                        {accountCustomFields.map((field, index) => {
                            const fieldData = customFieldsData.find((item) => item.customFieldTypeId === field.id);
                            return (
                                <BeneficiaryCustomFields
                                    key={`field_${fieldData?.customFieldTypeId ?? field.id}`}
                                    field={fieldData}
                                    data={data}
                                    fullFieldData={field}
                                    handleChange={handleChange}
                                    handleFileSaving={handleFileSaving}
                                    customFieldsData={customFieldsData}
                                    requiredFieldProps={requiredFieldProps}
                                    handleFieldError={handleFieldError}
                                />
                            );
                        })}
                    </Grid>
                </div>
            )}
        </div>
    );
};

export default BPAccount;
